import {IModifizierteTesthefteExport, IPortalseite, IWasserstandSchule, IWasserstandTestheftAnzahlen, IWasserstandTestheftstatistik} from "./Interfaces";
import Veraportal from "./Veraportal";
import VeraApi from "./VeraApi";
import Hauptmenu from "./Hauptmenu";
import {IFilterRule, TableFilter} from "./TableFilter";
import Utilities from "./Utilities";
import Modal from "./Modal";
import {ISchulenDB} from "./InterfacesDB";
import Tooltip from './Tooltip';

/**
 * Verwaltet die "Wasserstand"-Seite, die Administratoren einen Überblick über den Status
 * aller Schulen in einem Bundesland gibt. Zeigt Warnungen, Hinweise und allgemeine Fortschrittsdaten an.
 */
export default class Wasserstand extends Veraportal implements IPortalseite {
    private _schulen: IWasserstandSchule[] = [];
    private _schulenGefiltert: IWasserstandSchule[] = [];
    private _tableFilter: TableFilter = null; // Instanz zur Filterung und Sortierung der Schultabelle.
    private _lastInteractionSeconds: number = 0; // Zähler für die Zeit seit der letzten Benutzerinteraktion.
    private _schulstatusChart: echarts.ECharts = null;
    private _testheftstatistikChart: echarts.ECharts = null;

    private _loopInterval: number = 1000 * 5; // in Millisekunden

    private _anzahlInsgesamt: number = 0;
    private _anzahlAngezeigt: number = 0;
    private _anzahlMarkiert: number = 0;

    /**
     * Definiert die verschiedenen Benachrichtigungstypen (Warnungen, Hinweise) und deren Labels.
     * Wird zur dynamischen Berechnung und Anzeige von Benachrichtigungen verwendet.
     */
    private benachrichtigungstypenDefinition: { type: string; key: string; label: string; }[] = [
        {type: 'warnung', key: 'is_ok_schueler_ohne_geschlecht_warnung', label: 'Schüler ohne Geschlecht'},
        {type: 'warnung', key: 'is_ok_schueler_ohne_stammklasse_warnung', label: 'Schüler ohne Stammklasse'},
        {type: 'warnung', key: 'is_ok_stammklassen_ohne_schueler_warnung', label: 'Stammklassen ohne Schüler'},
        {type: 'warnung', key: 'is_ok_kurse_ohne_schueler_warnung', label: `${(this.sessionvariablen.schule.land !== 'ni') ? `Kurse` : `Lerngruppen`} ohne Schüler`},
        {type: 'warnung', key: 'is_ok_schueler_ohne_mathematik_testheft_warnung', label: 'Schüler ohne Mathe-Testheft'},
        {type: 'warnung', key: 'is_ok_schueler_ohne_deutsch_testheft_warnung', label: 'Schüler ohne Deutsch-Testheft'},
        {type: 'warnung', key: 'is_ok_schueler_ohne_englisch_testheft_warnung', label: 'Schüler ohne Englisch-Testheft'},
        {type: 'warnung', key: 'is_ok_schueler_ohne_franzoesisch_testheft_warnung', label: 'Schüler ohne Franz.-Testheft'},
        {type: 'warnung', key: 'is_ok_unvollstaendige_tests_mathematik_warnung', label: 'unvollst. Tests Mathe'},
        {type: 'warnung', key: 'is_ok_unvollstaendige_tests_deutsch_warnung', label: 'unvollst. Tests Deutsch'},
        {type: 'warnung', key: 'is_ok_unvollstaendige_tests_englisch_warnung', label: 'unvollst. Tests Englisch'},
        {type: 'warnung', key: 'is_ok_unvollstaendige_tests_franzoesisch_warnung', label: 'unvollst. Tests Französisch'}
    ];

    /**
     * Setzt die Schuldaten und berechnet dynamisch die Anzahl der Warnungen und Hinweise für jede Schule.
     * Löst anschließend eine Aktualisierung der Diagramme aus.
     * @param value - Ein Array von Schul-Objekten, das aus der Datenbank geladen wird.
     */
    set schulen(value: IWasserstandSchule[]) {
        this._schulen = value;

        this._schulen.forEach(s => {
            // Berechnet die Gesamtzahl der Warnungen für jede Schule basierend auf den Status-Flags.
            s.anzahlWarnungen = ((s.is_ok_schueler_ohne_geschlecht_warnung === 0) ? 1 : 0) +
                ((s.is_ok_schueler_ohne_stammklasse_warnung === 0) ? 1 : 0) +
                ((s.is_ok_stammklassen_ohne_schueler_warnung === 0) ? 1 : 0) +
                ((s.is_ok_kurse_ohne_schueler_warnung === 0) ? 1 : 0) +
                ((s.is_ok_schueler_ohne_mathematik_testheft_warnung === 0) ? 1 : 0) +
                ((s.is_ok_schueler_ohne_deutsch_testheft_warnung === 0) ? 1 : 0) +
                ((s.is_ok_schueler_ohne_englisch_testheft_warnung === 0) ? 1 : 0) +
                ((s.is_ok_schueler_ohne_franzoesisch_testheft_warnung === 0) ? 1 : 0) +
                ((s.is_ok_unvollstaendige_tests_mathematik_warnung === 0) ? 1 : 0) +
                ((s.is_ok_unvollstaendige_tests_deutsch_warnung === 0) ? 1 : 0) +
                ((s.is_ok_unvollstaendige_tests_englisch_warnung === 0) ? 1 : 0) +
                ((s.is_ok_unvollstaendige_tests_franzoesisch_warnung === 0) ? 1 : 0);

            // Berechnet die Gesamtzahl der Hinweise für jede Schule.
            s.anzahlHinweise = ((s.is_ok_unvollstaendige_tests_mathematik_hinweis === 0) ? 1 : 0) +
                ((s.is_ok_unvollstaendige_tests_deutsch_hinweis === 0) ? 1 : 0) +
                ((s.is_ok_unvollstaendige_tests_englisch_hinweis === 0) ? 1 : 0) +
                ((s.is_ok_unvollstaendige_tests_franzoesisch_hinweis === 0) ? 1 : 0);

            // Aktuell keine Tipps implementiert.
            s.anzahlTipps = 0;


            // Schüler ohne Anmeldung an verpflichtender Testung
            let susOhneVerpflichtendesTestheftArr: string[] = [];
            let ma: string[] = [];
            let de: string[] = [];
            let en: string[] = [];
            let fr: string[] = [];

            if (s.list_schueler_ohne_mathematik_testheft_warnung !== null)
                ma.push(...s.list_schueler_ohne_mathematik_testheft_warnung.split(',').map(i => i.trim()));
            if (s.list_schueler_ohne_deutsch_testheft_warnung !== null)
                de.push(...s.list_schueler_ohne_deutsch_testheft_warnung.split(',').map(i => i.trim()));
            if (s.list_schueler_ohne_englisch_testheft_warnung !== null)
                en.push(...s.list_schueler_ohne_englisch_testheft_warnung.split(',').map(i => i.trim()));
            if (s.list_schueler_ohne_franzoesisch_testheft_warnung !== null)
                fr.push(...s.list_schueler_ohne_franzoesisch_testheft_warnung.split(',').map(i => i.trim()));

            let itemsToRemove = new Set(fr);
            let ersteFremdspracheSonderfall: string[] = en.filter(item => !itemsToRemove.has(item));
            susOhneVerpflichtendesTestheftArr.push(...ma);
            susOhneVerpflichtendesTestheftArr.push(...de);
            susOhneVerpflichtendesTestheftArr.push(...ersteFremdspracheSonderfall);

            s.n_schueler_ohne_verpflichtende_testhefte = [...new Set(susOhneVerpflichtendesTestheftArr)].length ?? 0;
        });

        this.refreshPlots();
    }

    /**
     * Gibt die zwischengespeicherten Schuldaten zurück.
     */
    get schulen(): IWasserstandSchule[] {
        return this._schulen;
    }

    /**
     * Wird direkt nach dem Laden des DOMs auf jeder Portalseite ausgeführt.
     * Initialisiert grundlegende Funktionalitäten wie Sentry für das Fehler-Tracking,
     * das Hauptmenü und globale Event-Listener.
     */
    override async onPortalLoad(): Promise<boolean> {
        // Initialisiert Sentry (Glitchtip) für das Fehler-Tracking, falls aktiviert.
        // Sammelt Fehlermeldungen zur Verbesserung der Dienste.
        if (this.sessionvariablen.projekt.sentry_enabled === true) {
            try {
                // @ts-ignore
                Sentry.init({
                    dsn: "https://093f3f987b6c4cbeb2a688d27de5e4b4@sentry.projekt-vera8.de/1",
                    release: `${this.sessionvariablen.projekt.weiche}-portal@${this.sessionvariablen.projekt.portal_version_hash}`,
                    environment: (window.location.hostname.includes('localhost') || window.location.hostname.includes('127.0.0.1')) ? 'development' : 'production',
                    initialScope: {
                        tags: {
                            page_url: window.location.href,
                            schulnummer: this.sessionvariablen.schule.schulnummer,
                            user_id: this.sessionvariablen.user.user_id
                        },
                        user: {
                            id: `${this.sessionvariablen.schule.schulnummer} (User ${this.sessionvariablen.user.user_id})`
                        }
                    }
                });
            } catch {
                //
            }
        }

        // Initialisiert und aktualisiert das Hauptmenü des Wasserstandes.
        this.hauptmenu = Hauptmenu.getInstance(this);
        this.hauptmenu.refresh([
                {
                    bezeichnung: `Wasserstand ${this.sessionvariablen.land.kurzbezeichnung}`,
                    icon: `water`,
                    href: '/wasserstand.php',
                    target: null,
                    startTime: null,
                    endTime: null,
                    type: 'left',
                    submenu: []
                },
            {
                bezeichnung: `Ganze Schulliste\nexportieren`,
                icon: `filetype-csv`,
                id: 'schulliste-export-komplett-btn',
                target: null,
                startTime: null,
                endTime: null,
                type: 'left',
                submenu: []
            },
            {
                bezeichnung: `Bedarf Mod. TH\nexportieren`,
                icon: `universal-access-circle`,
                id: 'modifizierte-testhefte-export-btn',
                target: null,
                startTime: null,
                endTime: null,
                type: 'left',
                submenu: [],
                laender: ['nw']
            },
            ]
        );

        // Fügt Event-Listener zu internen Links hinzu, um die onPortalLeave-Methode
        // vor dem Navigieren aufzurufen und so einen sauberen Seitenwechsel zu gewährleisten.
        document.querySelectorAll('body a[href]:not([target])')
            .forEach((anchor) => {
                anchor.addEventListener('click', async (evt) => {
                    evt.preventDefault();
                    await this.lifecycleLeave();
                    let target = (evt.target as HTMLElement).closest(`a`) as HTMLAnchorElement;
                    window.location.href = target.href;
                });
            });


        // Startet einen Intervall-Timer, der die Zeit seit der letzten Benutzerinteraktion verfolgt.
        window.setInterval(() => {
            this._lastInteractionSeconds++;
        }, 1000);

        // Fügt einen Event-Listener zum Haupt-Container der Seite hinzu,
        // um Benutzerinteraktionen zu erkennen und den Interaktions-Timer zurückzusetzen.
        document.querySelector(`div#wasserstand`).addEventListener('click', this.resetInteractionTimer.bind(this));

        return true;
    }

    /**
     * Setzt den Interaktions-Timer zurück, wenn eine Benutzerinteraktion erkannt wird.
     * @private
     */
    private resetInteractionTimer(): void {
        this._lastInteractionSeconds = 0;
    }

    /**
     * Lifecycle-Methode, die nach dem vollständigen Laden des Portals ausgeführt wird.
     * Aktuell nicht in Verwendung.
     */
    override async onPortalFinished(): Promise<boolean> {
        return false;
    }

    /**
     * Lifecycle-Methode, die vor dem Verlassen des Portals ausgeführt wird.
     * Aktuell nicht in Verwendung.
     */
    protected async onPortalLeave(): Promise<boolean> {
        return false;
    }

    /**
     * Wird nach dem Laden der spezifischen "Wasserstand"-Seite ausgeführt.
     * Lädt die benötigten Schuldaten von der API, initialisiert den Tabellenfilter
     * und rendert die initiale Ansicht.
     */
    override async onSeiteLoad(): Promise<boolean> {
        // Initialisiert den Tabellenfilter für die Schultabelle.
        this._tableFilter = new TableFilter(
            this,
            {
                tableId: 'wasserstand',
                filterSlot: document.querySelector(`div#wasserstand > nav > div.filter`) as HTMLDivElement,
                filterCategories: [
                    {category: 'statustyp', dataType: 'number'},
                    {category: 'schulform', dataType: 'string', staticSortingOrder: ['GY', 'GE', 'RS', 'HS', 'FS']},
                    {category: 'anzahlWarnungen', labelCaption: 'Anzahl Warnungen', dataType: 'number'},
                    {category: 'anzahlHinweise', labelCaption: 'Anzahl Hinweise', dataType: 'number'},
                    ...this.benachrichtigungstypenDefinition.map(def => ({
                        category: def.key,
                        dataType: 'number'
                    }))
                ],
                defaultFilterRules: [
                    // {field: 'schulform', operator: 'equals', value: 'GE'},
                    // {field: 'statustyp', operator: 'greaterThan', value: 0}
                ],
                defaultSortingRules: [
                    // {category: 'statustyp', direction: 'asc'},
                    // {category: 'schulname', direction: 'asc'}
                ],
                predefinedFilterSets: [
                    {
                        "name": "ALLE SCHULEN ANZEIGEN",
                        "filterRules": [],
                        "sortingRules": []
                    },
                    {
                        name: 'Freie Schulform',
                        filterRules: [{field: 'schulform', operator: 'equals', value: 'null'}],
                        sortingRules: []
                    }, {
                        "name": "Alle nicht deaktivierten Schulen anzeigen",
                        "filterRules": [
                            {"field": "statustyp", "operator": "greaterThan", "value": "0"}
                        ],
                        "sortingRules": []
                    },
                    {
                        "name": "Alle Schulen OHNE EINGABEN im Portal",
                        "filterRules": [
                            {"field": "statustyp", "operator": "greaterThan", "value": "0"},
                            {"field": "n_schueler_insgesamt", "operator": "equals", "value": "0"}
                        ],
                        "sortingRules": [{"category": "schulform", "direction": "desc"}]
                    },
                    {
                        "name": "Verpfl. Schulen OHNE EINGABEN im Portal",
                        "filterRules": [
                            {"field": "statustyp", "operator": "equals", "value": "1"},
                            {"field": "n_schueler_insgesamt", "operator": "equals", "value": "0"}
                        ],
                        "sortingRules": [{"category": "schulform", "direction": "desc"}]
                    },
                    {
                        "name": "Verpfl. Schulen MIT WARNUNGEN",
                        "filterRules": [
                            {"field": "statustyp", "operator": "equals", "value": "1"},
                            {"field": "anzahlWarnungen", "operator": "greaterThan", "value": "0"}
                        ],
                        "sortingRules": [{"category": "schulform", "direction": "desc"}]
                    },
                    {
                        "name": "Alle Schulen MIT WARNUNGEN",
                        "filterRules": [
                            {"field": "statustyp", "operator": "greaterThan", "value": "0"},
                            {"field": "anzahlWarnungen", "operator": "greaterThan", "value": "0"}
                        ],
                        "sortingRules": [{"category": "schulform", "direction": "desc"}]
                    },
                    {
                        "name": "Alle Schulen mit VOLLSTÄNDIGEN DATEN",
                        "filterRules": [
                            {"field": "anzahlWarnungen", "operator": "equals", "value": "0"},
                            {"field": "n_schueler_insgesamt", "operator": "greaterThan", "value": "0"}
                        ],
                        "sortingRules": [{"category": "schulform", "direction": "desc"}]
                    },
                    ...(this.sessionvariablen.schule.land === 'nw' ?
                            [
                                {
                                    "name": "Verpfl. PP Schulen MIT WARNUNGEN",
                                    "filterRules": [
                                        {"field": "statustyp", "operator": "equals", "value": "1"},
                                        {"field": "anzahlWarnungen", "operator": "greaterThan", "value": "0"},
                                        {"field": "is_cbt_schule_mathematik", "operator": "equals", "value": "0"}
                                    ],
                                    "sortingRules": [{"category": "schulform", "direction": "desc"}]
                                },
                                {
                                    "name": "Verpfl. PP Schulen mit VOLLSTÄNDIGEN DATEN",
                                    "filterRules": [
                                        {"field": "statustyp", "operator": "equals", "value": "1"},
                                        {"field": "anzahlWarnungen", "operator": "equals", "value": "0"},
                                        {"field": "is_cbt_schule_mathematik", "operator": "equals", "value": "0"},
                                        {"field": "n_schueler_insgesamt", "operator": "greaterThan", "value": "0"}
                                    ],
                                    "sortingRules": [{"category": "schulform", "direction": "desc"}]
                                },
                                {
                                    "name": "Verpfl. CBT Schulen MIT WARNUNGEN",
                                    "filterRules": [
                                        {"field": "statustyp", "operator": "equals", "value": "1"},
                                        {"field": "anzahlWarnungen", "operator": "greaterThan", "value": "0"},
                                        {"field": "is_cbt_schule_mathematik", "operator": "equals", "value": "1"}
                                    ],
                                    "sortingRules": [{"category": "schulform", "direction": "desc"}]
                                },
                                {
                                    "name": "Verpfl. CBT Schulen mit VOLLSTÄNDIGEN DATEN",
                                    "filterRules": [
                                        {"field": "statustyp", "operator": "equals", "value": "1"},
                                        {"field": "anzahlWarnungen", "operator": "equals", "value": "0"},
                                        {"field": "is_cbt_schule_mathematik", "operator": "equals", "value": "1"},
                                        {"field": "n_schueler_insgesamt", "operator": "greaterThan", "value": "0"}
                                    ],
                                    "sortingRules": [{"category": "schulform", "direction": "desc"}]
                                }
                            ]
                            : []
                    ),
                ]
            }
        );


        // Startet den Seiten-Loop, der regelmäßig Daten abruft und die Ansicht aktualisiert.
        await this.pageLoop(true);

        return true;
    }

    /**
     * Wird ausgeführt, nachdem die "Wasserstand"-Seite vollständig initialisiert wurde.
     * Aktuell nicht in Verwendung.
     */
    override async onSeiteFinished(): Promise<boolean> {
        return true;
    }

    /**
     * Wird ausgeführt, bevor die "Wasserstand"-Seite verlassen wird.
     * Aktuell nicht in Verwendung.
     */
    override async onSeiteLeave(): Promise<boolean> {
        return true;
    }

    /**
     * Aktualisiert die Anzeige der Schulen und Diagramme basierend auf den aktuellen Filter- und Sortiereinstellungen.
     */
    public async refresh(): Promise<void> {
        this._anzahlInsgesamt = this.schulen.length;
        this._schulenGefiltert = this._tableFilter.filterAndSortData(this.schulen);
        this._anzahlAngezeigt = this._schulenGefiltert.length;
        await this.renderToDom(this._schulenGefiltert);
        await this.refreshNav();
        await this.refreshPlots();
    }

    /**
     * Berechnet die Daten für die Übersichtsdiagramme (Status nach Teilnahmeart, Häufigkeit der Benachrichtigungstypen)
     * und rendert diese mithilfe der ECharts-Bibliothek.
     */
    public async refreshPlots(): Promise<void> {
        this.diagrammSchulstatus();
        this.diagrammBenachrichtigungstypen();
        // this.diagrammTestheftstatistik(this.schulen);
    }

    private diagrammSchulstatus() {
        // Aggregiert Daten für das Diagramm "Status nach Teilnahmeart".
        let verpflichtend_warnungen = 0;
        let verpflichtend_hinweise = 0;
        let verpflichtend_abgeschlossen = 0;
        let verpflichtend_keine_schueler = 0;

        let freiwillig_warnungen = 0;
        let freiwillig_hinweise = 0;
        let freiwillig_abgeschlossen = 0;
        let freiwillig_keine_schueler = 0;

        this.schulen.forEach(s => {
            if (s.statustyp === 1) { // verpflichtend
                if (s.anzahlWarnungen > 0) {
                    verpflichtend_warnungen++;
                } else if (s.n_schueler_insgesamt === 0) {
                    verpflichtend_keine_schueler++;
                } else if (s.anzahlHinweise > 0) {
                    verpflichtend_hinweise++;
                } else {
                    verpflichtend_abgeschlossen++;
                }
            } else if (s.statustyp === 2) { // freiwillig
                if (s.anzahlWarnungen > 0) {
                    freiwillig_warnungen++;
                } else if (s.n_schueler_insgesamt === 0) {
                    freiwillig_keine_schueler++;
                } else if (s.anzahlHinweise > 0) {
                    freiwillig_hinweise++;
                } else {
                    freiwillig_abgeschlossen++;
                }
            }
        });

        // Berechnet die Gesamtzahlen und den maximalen Y-Achsenwert für die Diagramme.
        const verpflichtend_total = verpflichtend_abgeschlossen + verpflichtend_hinweise + verpflichtend_warnungen + verpflichtend_keine_schueler;
        const freiwillig_total = freiwillig_abgeschlossen + freiwillig_hinweise + freiwillig_warnungen + freiwillig_keine_schueler;
        const y_max = Math.ceil(Math.max(verpflichtend_total, freiwillig_total) * 1.1); // 10% Puffer nach oben.

        // Initialisiert das erste ECharts-Diagramm für den Schulstatus.
        const chartDom: HTMLDivElement = document.getElementById('diagramm-schulstatus') as HTMLDivElement;
        if (!this._schulstatusChart) {
            this._schulstatusChart = echarts.init(chartDom, '', {renderer: 'svg'});
            this._schulstatusChart.on('click', async (params) => {
                const seriesName = params.seriesName;
                const statustyp = (params.name === 'verpflichtend') ? 1 : 2;
                const newFilterRules: IFilterRule[] = [{field: 'statustyp', operator: 'equals', value: statustyp}];

                // Filter für Status
                if (seriesName === 'Schule hat Warnungen') {
                    newFilterRules.push({field: 'anzahlWarnungen', operator: 'greaterThan', value: 0});
                } else if (seriesName === 'Schule hat Hinweise') {
                    newFilterRules.push({field: 'anzahlWarnungen', operator: 'equals', value: 0});
                    newFilterRules.push({field: 'anzahlHinweise', operator: 'greaterThan', value: 0});
                } else if (seriesName === 'Schule abgeschlossen') {
                    newFilterRules.push({field: 'anzahlWarnungen', operator: 'equals', value: 0});
                    newFilterRules.push({field: 'anzahlHinweise', operator: 'equals', value: 0});
                    newFilterRules.push({field: 'n_schueler_insgesamt', operator: 'greaterThan', value: 0});
                } else if (seriesName === 'Schule komplett unbearbeitet') {
                    newFilterRules.push({field: 'n_schueler_insgesamt', operator: 'equals', value: 0});
                }
                this._tableFilter.filterRules = newFilterRules;
                this._tableFilter.refreshActiveFilters();
                await this.refresh();
            });

            // Passt die Größe des Diagramms bei Fensteränderungen an.
            window.addEventListener('resize', () => {
                this._schulstatusChart.resize();
            });
        }

        // Konfiguration für das Schulstatus-Diagramm.
        // @ts-ignore
        const option: EChartsOption = {
            color: ['rgba(35,190,0,0.5)', 'rgba(255,89,0,0.5)', 'rgba(196,0,6,0.5)', 'rgba(123,199,255,0.3)'],
            tooltip: {
                trigger: 'axis',
                axisPointer: {type: 'shadow'},
                confine: true
            },
            legend: {
                show: false
            },
            grid: [
                {
                    left: '15%',
                    top: '5%',
                    bottom: '10%',
                    width: '35%'
                }, // Grid für 'verpflichtend'.
                {
                    left: '48%',
                    top: '5%',
                    bottom: '10%',
                    width: '17.5%'
                }  // Grid für 'freiwillig'.
            ],
            xAxis: [
                {
                    type: 'category',
                    data: ['verpflichtend'],
                    gridIndex: 0,
                    axisTick: {alignWithLabel: true}
                },
                {
                    type: 'category',
                    data: ['freiwillig'],
                    gridIndex: 1,
                    axisTick: {alignWithLabel: true}
                }
            ],
            yAxis: [
                {
                    type: 'value',
                    gridIndex: 0,
                    max: y_max,
                    splitLine: {show: false} // Hintergrundlinien deaktivieren.
                },
                {
                    type: 'value',
                    gridIndex: 1,
                    max: y_max,
                    show: false // Zweite Y-Achse komplett ausblenden.
                }
            ],
            series: [
                {
                    name: 'Schule abgeschlossen',
                    type: 'bar',
                    stack: 'verpflichtend',
                    xAxisIndex: 0,
                    yAxisIndex: 0,
                    data: [verpflichtend_abgeschlossen]
                },
                {
                    name: 'Schule hat Hinweise',
                    type: 'bar',
                    stack: 'verpflichtend',
                    xAxisIndex: 0,
                    yAxisIndex: 0,
                    data: [verpflichtend_hinweise]
                },
                {
                    name: 'Schule hat Warnungen',
                    type: 'bar',
                    stack: 'verpflichtend',
                    xAxisIndex: 0,
                    yAxisIndex: 0,
                    data: [verpflichtend_warnungen]
                },
                {
                    name: 'Schule komplett unbearbeitet',
                    type: 'bar',
                    stack: 'verpflichtend',
                    xAxisIndex: 0,
                    yAxisIndex: 0,
                    data: [verpflichtend_keine_schueler]
                },
                {
                    name: 'Schule abgeschlossen',
                    type: 'bar',
                    stack: 'freiwillig',
                    xAxisIndex: 1,
                    yAxisIndex: 1,
                    data: [freiwillig_abgeschlossen]
                },
                {
                    name: 'Schule hat Hinweise',
                    type: 'bar',
                    stack: 'freiwillig',
                    xAxisIndex: 1,
                    yAxisIndex: 1,
                    data: [freiwillig_hinweise]
                },
                {
                    name: 'Schule hat Warnungen',
                    type: 'bar',
                    stack: 'freiwillig',
                    xAxisIndex: 1,
                    yAxisIndex: 1,
                    data: [freiwillig_warnungen]
                },
                {
                    name: 'Schule komplett unbearbeitet',
                    type: 'bar',
                    stack: 'freiwillig',
                    xAxisIndex: 1,
                    yAxisIndex: 1,
                    data: [freiwillig_keine_schueler]
                }
            ]
        };

        // Wendet die Konfiguration an und rendert das Diagramm.
        option && this._schulstatusChart.setOption(option);
    }

    private diagrammBenachrichtigungstypen() {
        // Zählt die Vorkommen jeder Warnung für das zweite Diagramm "Häufigkeit der Benachrichtigungstypen".
        const warnungsCounts = new Map<string, number>();
        this.benachrichtigungstypenDefinition.forEach(def => warnungsCounts.set(def.label, 0));

        this.schulen.forEach(schule => {
            this.benachrichtigungstypenDefinition.forEach(def => {
                // Wenn ein Warnungs-Flag auf 0 steht, bedeutet dies eine aktive Warnung.
                if (schule[def.key] === 0) {
                    warnungsCounts.set(def.label, warnungsCounts.get(def.label) + 1);
                }
            });
        });

        // Filtere Warnungen ohne Vorkommen und sortiere sie für eine übersichtliche Darstellung.
        const aktiveWarnungen = Array.from(warnungsCounts.entries())
            .filter(([, count]) => count > 0)
            .sort((a, b) => a[1] - b[1]);

        // Initialisiert das zweite ECharts-Diagramm für Benachrichtigungstypen, falls das DOM-Element existiert.
        const chartDom2: HTMLDivElement = document.getElementById('diagramm-benachrichtigungsstypen') as HTMLDivElement;
        if (chartDom2) {
            const myChart2 = echarts.init(chartDom2, '', {renderer: 'svg'});
            // Konfiguration für das Benachrichtigungstypen-Diagramm.
            // @ts-ignore
            const option2: EChartsOption = {
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {type: 'shadow'},
                    confine: true
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '3%',
                    containLabel: true
                },
                xAxis: {
                    type: 'value',
                    boundaryGap: [0, 0.01]
                },
                yAxis: {
                    type: 'category',
                    data: aktiveWarnungen.map(item => item[0]).slice(0, 8) // Zeigt die Top 8 Warnungen an.
                },
                series: [
                    {
                        name: 'Anzahl Schulen',
                        type: 'bar',
                        data: aktiveWarnungen.map(item => item[1]).slice(0, 8), // Zeigt die Anzahl der Schulen pro Warnungstyp an.
                        itemStyle: {
                            color: 'rgba(196,0,6,0.5)'
                        }
                    }
                ]
            };

            option2 && myChart2.setOption(option2);

            myChart2.on('click', async (params) => {
                // Finde die Definition der angeklickten Warnung
                const clickedLabel = params.name;
                const warnungsDefinition = this.benachrichtigungstypenDefinition.find(def => def.label === clickedLabel);

                if (warnungsDefinition) {
                    // Setze alle Filter zurück und wende den neuen an
                    this._tableFilter.filterRules = [];
                    this._tableFilter.addFilterRuleAndRefresh({
                        field: warnungsDefinition.key,
                        operator: 'equals',
                        value: 0
                    });
                }
            });

            // Passt die Größe des Diagramms bei Fensteränderungen an.
            window.addEventListener('resize', () => {
                myChart2.resize();
            });
        }
    }

    private diagrammTestheftstatistik(schulenArr: IWasserstandSchule[]) {

        // Testheftstatistik aus Schulen aggregieren
        const tmp: any = {};
        for (const school of schulenArr) {
            for (const [key, val] of Object.entries(school)) {
                if (typeof val !== 'number') continue;

                const [s, th, dom, m] = key.split(' ');
                const mode = m?.toLowerCase();

                if (mode !== 'pp' && mode !== 'cbt') continue;

                let diff = '';
                if (th === 'TH-A') diff = 'schwierigkeitA';
                else if (th === 'TH-B') diff = 'schwierigkeitB';
                else if (th === 'TH-C') diff = 'schwierigkeitC';
                else continue;

                const sub = s.toLowerCase();
                tmp[sub] ||= {};
                tmp[sub][dom] ||= {
                    domaene: dom,
                    schwierigkeitA: {pp: 0, cbt: 0},
                    schwierigkeitB: {pp: 0, cbt: 0},
                    schwierigkeitC: {pp: 0, cbt: 0}
                };
                tmp[sub][dom][diff][mode] += val;
            }
        }

        const testheftstatistik: any = {};
        for (const sub in tmp) {
            testheftstatistik[sub] = {varianten: Object.values(tmp[sub])};
        }

        // THE CHART CODE STARTS HERE
        const subjects = ['ma', 'de', 'en', 'fr'];

        // Helper to sum up values across all domains for a subject
        const getCounts = (sub: string, type: 'pp' | 'cbt', diff: 'schwierigkeitA' | 'schwierigkeitB' | 'schwierigkeitC') => {
            if (!testheftstatistik[sub]) return 0;
            return testheftstatistik[sub].varianten.reduce((acc: number, v: any) => {
                return acc + (v[diff] && v[diff][type] ? v[diff][type] : 0);
            }, 0);
        };

        const dataPP_A = subjects.map(s => getCounts(s, 'pp', 'schwierigkeitA'));
        const dataPP_B = subjects.map(s => getCounts(s, 'pp', 'schwierigkeitB'));
        const dataPP_C = subjects.map(s => getCounts(s, 'pp', 'schwierigkeitC'));

        const dataCBT_A = subjects.map(s => getCounts(s, 'cbt', 'schwierigkeitA'));
        const dataCBT_B = subjects.map(s => getCounts(s, 'cbt', 'schwierigkeitB'));
        const dataCBT_C = subjects.map(s => getCounts(s, 'cbt', 'schwierigkeitC'));

        // Normalize to 100%
        const normalize = (a: number, b: number, c: number) => {
            const sum = a + b + c;
            if (sum === 0) return [0, 0, 0];
            const pa = Math.round((a / sum) * 100);
            const pb = Math.round((b / sum) * 100);
            const pc = Math.round((c / sum) * 100);
            // Note: Sum might not be exactly 100 due to rounding, but close enough for visual representation
            return [pa, pb, pc];
        };

        const finalPP_A: number[] = [];
        const finalPP_B: number[] = [];
        const finalPP_C: number[] = [];
        const finalCBT_A: number[] = [];
        const finalCBT_B: number[] = [];
        const finalCBT_C: number[] = [];

        for (let i = 0; i < subjects.length; i++) {
            const [ppa, ppb, ppc] = normalize(dataPP_A[i], dataPP_B[i], dataPP_C[i]);
            finalPP_A.push(ppa);
            finalPP_B.push(ppb);
            finalPP_C.push(ppc);

            const [cbta, cbtb, cbtc] = normalize(dataCBT_A[i], dataCBT_B[i], dataCBT_C[i]);
            finalCBT_A.push(cbta);
            finalCBT_B.push(cbtb);
            finalCBT_C.push(cbtc);
        }

        const chartDom = document.getElementById('diagramm-testheftstatistik') as HTMLDivElement;
        if (!chartDom) return;

        if (!this._testheftstatistikChart) {
            this._testheftstatistikChart = echarts.init(chartDom, '', {renderer: 'svg'});
            window.addEventListener('resize', () => {
                this._testheftstatistikChart && this._testheftstatistikChart.resize();
            });
        }

        // Colors: Light (A) -> Medium (B) -> Dark (C)
        const colors: Record<string, string[]> = {
            'ma': ['#90caf9', '#42a5f5', '#1565c0'], // Blue
            'de': ['#ffcc80', '#ffa726', '#ef6c00'], // Orange
            'en': ['#a5d6a7', '#66bb6a', '#2e7d32'], // Green
            'fr': ['#e1bee7', '#ab47bc', '#7b1fa2'], // Purple
        };

        const getColor = (idx: number, level: number) => {
            const sub = subjects[idx];
            return colors[sub][level];
        };

        const option: any = {
            tooltip: {
                trigger: 'axis',
                axisPointer: {type: 'shadow'},
                valueFormatter: (value: number) => value + ' %'
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '10%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: ['Mathematik', 'Deutsch', 'Englisch', 'Französisch']
            },
            yAxis: {
                type: 'value',
                max: 100,
                axisLabel: {
                    formatter: '{value} %'
                }
            },
            series: [
                // PP Stack
                {
                    name: 'Schwierigkeit A',
                    type: 'bar',
                    stack: 'pp',
                    data: finalPP_A,
                    itemStyle: {color: (params: any) => getColor(params.dataIndex, 0)},
                    label: {show: true, position: 'bottom', formatter: 'PP', color: '#000', distance: 5}
                },
                {
                    name: 'Schwierigkeit B',
                    type: 'bar',
                    stack: 'pp',
                    data: finalPP_B,
                    itemStyle: {color: (params: any) => getColor(params.dataIndex, 1)}
                },
                {
                    name: 'Schwierigkeit C',
                    type: 'bar',
                    stack: 'pp',
                    data: finalPP_C,
                    itemStyle: {color: (params: any) => getColor(params.dataIndex, 2)}
                },
                // CBT Stack
                {
                    name: 'Schwierigkeit A',
                    type: 'bar',
                    stack: 'cbt',
                    data: finalCBT_A,
                    itemStyle: {color: (params: any) => getColor(params.dataIndex, 0)},
                    label: {show: true, position: 'bottom', formatter: 'CBT', color: '#000', distance: 5}
                },
                {
                    name: 'Schwierigkeit B',
                    type: 'bar',
                    stack: 'cbt',
                    data: finalCBT_B,
                    itemStyle: {color: (params: any) => getColor(params.dataIndex, 1)}
                },
                {
                    name: 'Schwierigkeit C',
                    type: 'bar',
                    stack: 'cbt',
                    data: finalCBT_C,
                    itemStyle: {color: (params: any) => getColor(params.dataIndex, 2)}
                }
            ]
        };

        this._testheftstatistikChart.setOption(option);

    }

    /**
     * Rendert die gefilterte und sortierte Liste der Schulen in das DOM.
     * Initialisiert zudem alle notwendigen UI-Elemente und Event-Listener für die Interaktion.
     * @param schulenGefiltert - Das Array der zu rendernden Schulen.
     */
    private async renderToDom(schulenGefiltert: IWasserstandSchule[]): Promise<void> {
        let schulenContainer = document.querySelector(`div#wasserstand > div.container`) as HTMLDivElement;

        // Erzeugt das HTML für jede Schule basierend auf den gefilterten Daten.
        let nSelects = 0;
        schulenContainer.innerHTML = schulenGefiltert.map(s => {
            nSelects++;
            const schulformAnzeige = s.schulform === null ? 'Freie Schulform' : (s.schulform ?? '-');
            const schulformBezeichnung: string = ({
                GY: 'Gymnasium',
                GE: 'Gesamtschule, Gemeinschaftsschule oder Schule mit Kurssystem',
                RS: 'Realschule oder Realschule plus',
                HS: 'Hauptschule',
                FS: 'Förderschule',
                GS: 'Grundschule'
            } as Record<string, string>)[s.schulform] ?? schulformAnzeige;

            return `
            <div class="schule"
                data-ausgeklappt="0"
                data-markiert="0"
                data-changed="0"
                data-schulnummer="${s.schulnummer}"
                ${((s.email ?? null) !== null) ? `data-email="${s.email}"` : ``}
                data-warnungen="${s.anzahlWarnungen}" data-hinweise="${s.anzahlHinweise}"
                data-statustyp="${s.statustyp}">
                <div class="immer-sichtbar">
                    ${(false) ? `
                    <div class="caret">
                        <svg role="graphics-symbol" class="caret-right">
                            <use href="/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#caret-right-fill"/>
                        </svg>
                        <svg role="graphics-symbol" class="caret-down">
                            <use href="/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#caret-down-fill"/>
                        </svg>
                    </div>
                    ` : ``}
                    <div class="markierung" data-tooltip="${Tooltip.html`
                        <p><strong>Schule markieren</strong></p>
                        <p>Markieren Sie mehrere Schulen, um gemeinsame Aktionen auszuführen.</p>
                    `}"><label for="markierung-${nSelects}"><input type="checkbox" class="markierung" id="markierung-${nSelects}"/></label></div>
        
                    <div class="schulnummer">${s.schulnummer}</div>
                    <div class="schulform ${s.schulform === null ? 'freie-schulform' : ''}" data-tooltip="${Tooltip.html`
                        <p><strong>${s.schulform === null ? 'Freie Schulform' : `Schulform ${schulformAnzeige}`}</strong></p>
                        <p>${schulformBezeichnung}</p>
                    `}">
                        ${schulformAnzeige}
                    </div>
                    ${(false && this.sessionvariablen.schule.land === 'nw') ? `
                    <div class="standorttyp">
                        ${(s.standorttyp_nw !== null) ? `Standorttyp<span class="standorttyp">${s.standorttyp_nw ?? '-'}</span>` : `-`}
                    </div>
                    ` : ``}
                    ${(this.sessionvariablen.schule.land === 'nw') ? `
                    <div class="durchfuehrungsmodus">
                        Testmodus
                        <span class="durchfuehrungsmodus">${(s.is_cbt_schule_mathematik === 1) ? 'CBT' : (s.is_cbt_schule_mathematik === 0) ? 'PP' : '??'}</span>
                    </div>
                    ` : ``}
                    <div class="statustyp">
                        <label for="statustyp-select-${nSelects}">Schulstatus:</label>
                        <select class="statustyp-select small" id="statustyp-select-${nSelects}">
                            <option value="1" ${(s.statustyp === 1) ? `selected` : ``}><span class="verpflichtend">verpflichtend</span></option>
                            <option value="2" ${(s.statustyp === 2) ? `selected` : ``}>freiwillig</option>
                            <option value="0" ${(s.statustyp === 0) ? `selected` : ``}>deaktiviert</option>
                        </select>
                    </div>
                    <div class="adresse">
                        <div>
                            <span class="schulname">${s.schulname ?? '-'}</span><br/>
                            <span class="strasse">${s.strasse_nr ?? ''}</span>,
                            <span class="plz">${s.plz ?? ''}</span>
                            <span class="ort">${s.ort ?? '-'}</span>
                        </div>
                    </div>
                    <div class="schulstatus">
                        <div class="anzahl-schueler" data-tooltip="${Tooltip.html`
                            <p><strong>Angelegte Schülerinnen und Schüler</strong></p>
                            <p>Insgesamt <strong>${s.n_schueler_insgesamt}</strong></p>
                        `}">
                            Schüleranzahl<span class="anzahl-schueler">${s.n_schueler_insgesamt}</span>
                        </div>
                        <div class="anzahl-stammklassen" data-tooltip="${Tooltip.html`
                            <p><strong>Angelegte Stammklassen</strong></p>
                            <p>Insgesamt <strong>${s.n_stammklassen_insgesamt}</strong></p>
                        `}">
                            Stammklassen<span class="anzahl-stammklassen">${s.n_stammklassen_insgesamt}</span>
                        </div>
                        <div class="anzahl-kurse" data-tooltip="${Tooltip.html`
                            <p><strong>Angelegte ${(this.sessionvariablen.schule.land !== 'ni') ? 'Kurse' : 'Lerngruppen'}</strong></p>
                            <p>Gesamtzahl über Mathematik, Deutsch, Englisch und Französisch.</p>
                        `}">
                            ${(this.sessionvariablen.schule.land !== 'ni') ? `Kurse` : `Lerngruppen`} insg.<span class="anzahl-kurse">${(this.sessionvariablen.schule.land !== 'ni') ? `${s.n_kurse_insgesamt}` : `${s.n_lerngruppen_insgesamt}`}</span>
                        </div>
                        <div class="benachrichtigungen">
                            Benachrichtigungen<span class="benachrichtigungen">
                                <span class="warnungen" data-tooltip="${Tooltip.html`
                                    <p><strong>${s.anzahlWarnungen} Warnungen</strong></p>
                                    <p>Weitere Informationen finden Sie im ausgeklappten Schuleintrag.</p>
                                `}">${s.anzahlWarnungen}</span>
                                -
                                <span class="hinweise" data-tooltip="${Tooltip.html`
                                    <p><strong>${s.anzahlHinweise} Hinweise</strong></p>
                                    <p>Weitere Informationen finden Sie im ausgeklappten Schuleintrag.</p>
                                `}">${s.anzahlHinweise}</span>
                                -
                                <span class="tipps" data-tooltip="${Tooltip.html`
                                    <p><strong>${s.anzahlTipps} Tipps</strong></p>
                                    <p>Diese Funktion ist derzeit noch nicht umgesetzt.</p>
                                `}">${s.anzahlTipps}</span>
                            </span>
                        </div>
                    </div>
                    <div class="optionen">
                        
                        ${(this.sessionvariablen.schule.land === 'ni') ? `
                        <button type="button" class="passwort-aendern-btn secondary icon-only"
                        data-username="${s.username_hauptnutzer}"
                        aria-label="Passwortanforderungslink für Schule ${s.schulnummer} generieren"
                        data-tooltip="${Tooltip.html`
                            <p><strong>Passwortanforderungslink generieren</strong></p>
                            <p>Erstellt einen neuen Link für den Koordinatorenzugang der Schule <strong>${s.schulnummer}</strong> und zeigt ihn zum Versand an.</p>
                        `}">
                            <svg role='graphics-symbol'>
                                <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#unlock'/>
                            </svg>
                        </button>
                        ` : ``}
                        <button type="button" class="portal-login-btn secondary" data-username="${s.username_hauptnutzer}">
                            <svg role='graphics-symbol'>
                                <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#box-arrow-in-right'/>
                            </svg>
                            Login im<br/>${this.sessionvariablen.projekt.bezeichnung}-Portal
                        </button>
                    </div>
                </div>
                <div class="ausklappbar">
                    <div class="schule">
                        <h4>Kontaktinformationen</h4>
                        <div>
                            <dt>E-Mail:</dt>
                            <dd>${(s.email !== null) ? `<a class="email" href="mailto:${s.email}">${s.email}</a>` : `-`}</dd>
                            <dt>Telefon:</dt>
                            <dd>${(s.telefon !== null) ? `<span class="telefon">${s.telefon}</span>` : `-`}</dd>
                        </div>
                    </div>
                    <div class="benachrichtigungen">
                        <h4>Benachrichtigungen</h4>
                        ${((s.anzahlWarnungen + s.anzahlHinweise) !== 0) ? `
                        <div class="container">
            ${this.benachrichtigungstypenDefinition.map(w => {
                // Zeigt nur Benachrichtigungen an, die aktiv sind (Schulwert ist 0).
                if (s[w.key] === 0)
                    return `<span class="${w.type}">
                                <svg role='graphics-symbol'>
                                    <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#x-lg'/>
                                </svg>
                                ${w.label}
                            </span>`
            }).join('')}
                        </div>
                        ` : `alle Eingaben im Portal sind vollständig`}
                    </div>
                </div>
            </div>
            `;
        }).join('');

        this.initUiStyling();

        // Initialisiert die TomSelect-Dropdowns für die Statusauswahl jeder Schule.
        try {
            let selectArr: NodeListOf<HTMLSelectElement> = document.querySelectorAll(`select.statustyp-select`);
            selectArr.forEach(s => {
                // @ts-ignore
                // new TomSelect(`#${s.id}`, {
                //     create: false,
                //     allowEmptyOption: true,
                //     controlInput: null,
                //     searchField: [],
                //     render: {
                //         // Rendert jede Option im Dropdown-Menü.
                //         option: function (data, escape) {
                //             return `<div><span class="${escape(data.text)}">${escape(data.text)}</span></div>`;
                //         },
                //         // Rendert das ausgewählte Element im Eingabefeld.
                //         item: function (data, escape) {
                //             return `<div><span class="${escape(data.text)}">${escape(data.text)}</span></div>`;
                //         }
                //     }
                // });

                // Fügt einen Event-Listener hinzu, um Änderungen am Schulstatus zu verfolgen.
                s.addEventListener('change', (evt) => {
                    let target: HTMLSelectElement = evt.target as HTMLSelectElement;
                    if (target.value !== '-') {
                        s.closest(`div.schule`).setAttribute('data-changed', '1');
                    }
                });
            });
        } catch (e) {
            Utilities.errorToSentry(e);
        }

        // Fügt den Event-Listener zum Auf- und Zuklappen der Detailansicht jeder Schule hinzu.
        document.querySelectorAll(`div.schule[data-ausgeklappt]`)
            .forEach(s => {
                // s.addEventListener('click',
                //     e => {
                //         let target = (e.target as HTMLElement);
                //         // Verhindert das Auf-/Zuklappen, wenn auf interaktive Elemente geklickt wird.
                //         if (target.closest(`button, input, a, label`))
                //             return;
                //
                //         let schuleDiv = target.closest(`div.schule[data-ausgeklappt]`);
                //         schuleDiv.setAttribute(
                //             'data-ausgeklappt',
                //             (schuleDiv.getAttribute('data-ausgeklappt') === '0') ? '1' : '0'
                //         );
                //     }
                // );
                }
            );

        const bulkselectNavigationAktualisieren = () => {
            this._anzahlMarkiert = document.querySelectorAll(`div.schule[data-markiert='1']`).length;
            (document.querySelector(`span#markierte-schulen`) as HTMLSpanElement).innerHTML = this._anzahlMarkiert.toString();

            if (this._anzahlMarkiert === 0) {
                document.querySelector(`div#mit-ausgewaehlten`).classList.remove('active');
            } else {
                document.querySelector(`div#mit-ausgewaehlten`).classList.add('active');
            }
        };
        const einmaligBulkselectNavigationAktualisieren = Utilities.debounce(bulkselectNavigationAktualisieren, 250);

        // Fügt den Event-Listener für die Markierungs-Checkboxen hinzu.
        document.querySelectorAll(`div.schule[data-markiert] div.markierung input[type='checkbox']`)
            .forEach(s => {
                    s.addEventListener('change',
                        e => {
                            let target = (e.target as HTMLInputElement);
                            let schuleDiv = target.closest(`div.schule[data-markiert]`);
                            schuleDiv.setAttribute(
                                'data-markiert',
                                (target.checked === true) ? '1' : '0'
                            );

                            einmaligBulkselectNavigationAktualisieren();
                        }
                    );
                }
            );

        // Event-Listener für alle Select-Felder innerhalb einer Schule.
        // Wenn eine Schule markiert ist, werden Änderungen an einem Select-Feld
        // auf alle anderen markierten Schulen mit dem gleichen Select-Typ übertragen (Bulk-Edit).
        document.querySelectorAll(`div.schule select`)
            .forEach(selectElement => {
                selectElement.addEventListener('change', (e) => {
                    try {
                        const target = e.target as HTMLSelectElement;
                        const schule: HTMLDivElement = target.closest('div.schule');
                        // Überprüft, ob die Schule markiert ist, um Bulk-Änderungen zu ermöglichen.
                        if (schule?.getAttribute('data-markiert') === '1') {
                            const classList = target.className.toString().split(' ');
                            if (classList.length > 0) {
                                // Selektiert alle markierten Schulen mit dem gleichen Select-Typ.
                                const selector = `div.schule[data-markiert='1'] select.${classList.join('.')}`;

                                document.querySelectorAll(selector).forEach(control => {
                                    if (control.id !== target.id) {
                                        // @ts-ignore
                                        // control.tomselect.setValue(target.value, true);
                                        control.value = target.value;
                                        control.closest(`div.schule`).setAttribute('data-changed', '1');
                                    }
                                });
                            }
                        }
                    } catch (e) {
                        Utilities.errorToSentry(e);
                    }
                });
            });

        /**
         * Event-Listener für den "Login im VERA-Portal"-Button.
         * Ruft ein Single-Auth-Token ab und öffnet das entsprechende Schulportal in einem neuen Tab.
         * Deaktiviert den Button während des Login-Vorgangs.
         */
        document.querySelectorAll(`div.schule button.portal-login-btn`)
            .forEach(b => b
                .addEventListener('click',
                    async e => {
                        let target = (e.target as HTMLButtonElement);
                        let username: string = target.getAttribute('data-username') ?? null;

                        if (username === null)
                            return;

                        target.disabled = true; // Deaktiviert den Button, um Mehrfachklicks zu verhindern.
                        const api = VeraApi.getInstance(this);
                        let singleAuthToken: string = await api.getSingleAuthToken(username);

                        let domainParts = Utilities.getDomainParts();
                        window.open(`https://einsicht.${domainParts['noSubdomain']}/?singleAuthToken=${singleAuthToken}`, 'wasserstand-einsicht');
                        await Utilities.delay(5000); // Wartet 5 Sekunden, bevor der Button wieder aktiviert wird.
                        target.disabled = false;
                    }
                )
            );

        // Passwortanforderungslink generieren
        if (this.sessionvariablen.schule.land === 'ni')
            document.querySelectorAll(`div.schule button.passwort-aendern-btn`)
                .forEach(b => b
                    .addEventListener('click',
                        async e => {
                            let target = (e.target as HTMLButtonElement);
                            let username: string = target.getAttribute('data-username') ?? null;

                            if (username === null)
                                return;

                            target.disabled = true; // Deaktiviert den Button, um Mehrfachklicks zu verhindern.
                            const api = VeraApi.getInstance(this);
                            let passwortAnforderung: { passwort_anforderung_hash: string } = await api.generatePasswortanforderungLink(username);

                            if ((passwortAnforderung?.passwort_anforderung_hash ?? null) === null) {
                                target.disabled = false;
                                return;
                            }

                            let hinweisModal = new Modal(this);
                            hinweisModal.show({
                                title: `Neuer Passwortanforderungslink`,
                                body: `
                                    <p>
                                        Ein neuer Passwortanforderungslink für den Koordinatorenzugang <i>${username}</i> wurde in der Datenbank hinterlegt und kann jetzt übermittelt werden.
                                        Er kann mit einem Klick darauf in die Zwischenablage gespeichert werden:
                                    </p>
                                    <span onclick="navigator.clipboard.writeText(this.textContent)"
                                         style="white-space: nowrap; font-size: .8em; cursor: pointer;"
                                        data-tooltip="${Tooltip.html`
                                            <p><strong>Link kopieren</strong></p>
                                            <p>Klicken Sie auf den Link, um ihn in die Zwischenablage zu kopieren.</p>
                                        `}">https://${this.sessionvariablen.projekt.projektdomain}/passwort-neu-generieren.php?hash=${passwortAnforderung.passwort_anforderung_hash}</span>
                                `,
                                hasSubmit: false,
                                hasCancel: true,
                                hasBestaetigung: false,
                                cancelText: 'OK',
                                width: 800
                            });
                            await Utilities.delay(3000); // Wartet 3 Sekunden, bevor der Button wieder aktiviert wird.
                            target.disabled = false;
                        }
                    )
                );
    }

    /**
     * Sendet alle im DOM markierten und geänderten Schulstatus-Informationen an die API.
     * Setzt den 'data-changed'-Status der verarbeiteten Schulen zurück.
     * @returns {Promise<boolean>} True, wenn Änderungen erfolgreich übertragen wurden, sonst False.
     */
    private async pushChanges(): Promise<boolean> {
        // Findet alle Schulen im DOM, die als geändert markiert sind.
        let changesDom: NodeListOf<HTMLDivElement> = document.querySelectorAll(`div#wasserstand div.schule[data-changed='1']`);
        if (changesDom.length === 0)
            return false;

        let changes: { schulnummer: string, statustyp: number }[] = [];
        // Sammelt die Schulnummern und die neuen Status-Typen der geänderten Schulen.
        changesDom.forEach(s => {
            let schulstatusDom: HTMLSelectElement = s.querySelector(`select.statustyp-select`);
            let schulnummer = s.getAttribute('data-schulnummer') ?? null;
            let statustyp = schulstatusDom.value ?? null;

            if (schulnummer === null || statustyp === null)
                return;

            changes.push({
                schulnummer: schulnummer,
                statustyp: parseInt(statustyp)
            });
        });

        // Sendet die gesammelten Änderungen an die API.
        const api = VeraApi.getInstance(this);
        let response = await api.postSchulstatus(changes);

        if (response === true) {
            console.log('Änderungen erfolgreich gespeichert!');
        }

        // Setzt den 'data-changed'-Status der verarbeiteten Schulen zurück.
        changesDom.forEach(s => {
            s.setAttribute('data-changed', '0');
        });

        return response
    }

    /**
     * Führt einen regelmäßigen Seiten-Loop aus, um Änderungen zu speichern und die Daten zu aktualisieren.
     * Der Loop wird nur ausgeführt, wenn seit der letzten Interaktion 5 Sekunden vergangen sind oder es die erste Iteration ist.
     * @param {boolean} firstIteration - Gibt an, ob es sich um die erste Ausführung des Loops handelt.
     */
    private async pageLoop(firstIteration: boolean = false): Promise<void> {
        // Führt den Loop nur aus, wenn der Benutzer inaktiv war oder es die erste Initialisierung ist.
        if (this._lastInteractionSeconds >= 5 || firstIteration === true) {
            let changed: boolean = await this.pushChanges(); // Speichert ausstehende Änderungen.

            if (changed === true || firstIteration === true) {
                // Ruft die aktuellen Schuldaten von der API ab.
                const api = VeraApi.getInstance(this);
                const [wasserstandSchulen] =
                    await Promise.all([
                        api.fetchWasserstandSchulen()
                    ]);
                this.schulen = wasserstandSchulen; // Aktualisiert die Schuldaten.
                this._anzahlInsgesamt = this.schulen.length;

                // Rendert die aktualisierten Schuldaten in das DOM.
                this._schulenGefiltert = this._tableFilter.filterAndSortData(this.schulen);
                this._anzahlAngezeigt = this._schulenGefiltert.length;
                await this.renderToDom(this._schulenGefiltert);
                await this.refreshNav();
            }
        }

        // Plant die nächste Ausführung des Seiten-Loops.
        if (this._loopInterval !== null) {
            window.setTimeout(() => {
                this.pageLoop(false);
            }, this._loopInterval);
        }
    }

    private async refreshNav() {
        document.querySelector(`#wasserstand > nav > div.options`).innerHTML = `
        <div id="angezeigte-schulen">
            ${this._anzahlAngezeigt} von ${this._anzahlInsgesamt} Schulen angezeigt
        </div>
        <button id="alle-auswaehlen-btn" class="secondary" aria-label="Alle angezeigten Schulen aus- oder abwählen" data-tooltip="${Tooltip.html`
            <p><strong>Alle angezeigten Schulen markieren</strong></p>
            <p>Wählt alle derzeit angezeigten Schulen aus oder hebt deren Auswahl auf.</p>
        `}">
            <svg role='graphics-symbol'>
                <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#check-all'/>
            </svg>
            alle Angezeigten<br/>aus-/abwählen
        </button>
        <button id="schulliste-export-gefiltert-btn" class="secondary" aria-label="Gefilterte Schulliste exportieren" data-tooltip="${Tooltip.html`
            <p><strong>Gefilterte Schulliste exportieren</strong></p>
            <p>Exportiert die derzeit angezeigten Schulen als CSV-Datei.</p>
        `}">
            <svg role='graphics-symbol'>
                <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#filetype-csv'/>
            </svg>
            gefilterte Schulliste<br/>exportieren
        </button>

        <div id="mit-ausgewaehlten" class="auswahltoggle">
            <span class="bezeichnung"><span id="markierte-schulen"></span>markierte Schulen:</span>
            <button id="mailadressen-kopieren-btn" class="secondary" aria-label="Mailadressen markierter Schulen kopieren" data-tooltip="${Tooltip.html`
                <p><strong>Mailadressen kopieren</strong></p>
                <p>Kopiert die Mailadressen aller markierten Schulen in die Zwischenablage.</p>
            `}">
                <svg role='graphics-symbol'>
                    <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#envelope-check'/>
                </svg>
                Mailadressen kopieren
            </button>
            <button id="mail-versenden-btn" class="disabled secondary" aria-label="Mail an markierte Schulen versenden" data-tooltip="${Tooltip.html`
                <p><strong>Mail versenden</strong></p>
                <p>Erstellt eine Nachricht an die markierten Schulen.</p>
            `}">
                <svg role='graphics-symbol'>
                    <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#envelope-at'/>
                </svg>
                Mail versenden ...
            </button>
        </div>
        `;

        // Alle auswählen / abwählen Button
        document.querySelector(`button#alle-auswaehlen-btn`)
            ?.addEventListener('click', evt => {
                let checkOrUncheck: boolean = (this._anzahlMarkiert === 0);
                document.querySelectorAll(`div.schule div.markierung input[type='checkbox']`)
                    ?.forEach((c: HTMLInputElement) => {
                        c.checked = checkOrUncheck;
                        c.dispatchEvent(new Event('change'));
                    });
            });

        // "Mailadressen kopieren"-Button
        document.querySelector(`button#mailadressen-kopieren-btn`)
            ?.addEventListener('click', async (evt) => {

                try {
                    let mailArr: string[] = [];
                    document.querySelectorAll(`div.schule div.markierung input[type='checkbox']:checked`)
                        ?.forEach((c: HTMLInputElement) => {
                            let mail: string = c.closest(`div.schule[data-email]`)?.getAttribute('data-email') ?? null;
                            if (mail === null)
                                return;

                            mailArr.push(mail);
                        });

                    await navigator.clipboard.writeText(mailArr.join(';'));

                    let hinweisModal = new Modal(this);
                    await hinweisModal.show({
                        title: `Mailadressen in Zwischenablage kopiert`,
                        body: `
                        <p>
                            Die bei uns hinterlegten Schul-Mailadressen aller ${this._anzahlMarkiert} markierten Schulen wurden in die Zwischenablage kopiert und können einfach per <i>STRG+V</i>
                            im Mailprogramm (z.B. <i>Outlook</i>, <i>Thunderbird</i>) in das BCC-Feld (Datenschutzkonforme Blindkopie) eingefügt werden.
                        </p>
                        
                        ${(this._anzahlMarkiert !== mailArr.length) ? `
                        <p>
                            Zu ${(this._anzahlMarkiert - mailArr.length)} markierten Schulen wurden keine Schul-Mailsadressen bei uns in der Datenbank hinterlegt. Sie befinden sich folglich nicht unter den Adressen in der Zwischenablage. 
                        </p>
                        ` : ``}
                        `,
                        hasSubmit: false,
                        hasCancel: true,
                        hasBestaetigung: false,
                        width: 500,
                        cancelText: 'OK'
                    });
                } catch {
                    //
                }

            });

        // "Mail versenden"-Button
        document.querySelector(`button#mail-versenden-btn`)
            ?.addEventListener('click', evt => {

            });

        // "Aktuell gefilterte Liste herunterladen"-Button


        // document.querySelector(`#druckbedarfsexport-btn`)
        document.querySelector(`#schulliste-export-komplett-btn`)
            ?.addEventListener('click', () => {
                this.schulenToCsv(this.schulen, 'komplett');
            });

        document.querySelector(`#schulliste-export-gefiltert-btn`)
            ?.addEventListener('click', () => {
                this.schulenToCsv(this._schulenGefiltert, 'gefiltert');
            });

        document.querySelector(`#modifizierte-testhefte-export-btn`)
            ?.addEventListener('click', async () => {
                const api = VeraApi.getInstance(this);
                let modifizierteTesthefte: IModifizierteTesthefteExport[] = await api.fetchModifizierteTesthefte();
                if ((modifizierteTesthefte ?? []).length === 0)
                    return;

                Utilities.exportJsonToCsv(modifizierteTesthefte.map(s => {
                    return {
                        'Schulnummer': s.schulnummer,
                        'Schulname': this.schulen.filter(s2 => s2.schulnummer === s.schulnummer)[0]?.schulname ?? '-',
                        'Sehbehindert': s.sehbehindert,
                        'Blind': s.blind,
                        'Gehoerlos': s.gehoerlos,
                        'Schwerhoerig': s.schwerhoerig,
                        'Sprache': s.sprache
                    }
                }), `vera8-modifizierte-testhefte.csv`, '\t');
            });

    }

    private getTestheftExportbezeichner(testheft: IWasserstandTestheftstatistik): string {
        let bezeichnung = testheft.bezeichnung_kurz ?? testheft.bezeichnung;
        let domaenen: string[] = [];
        if (this.sessionvariablen.projekt.weiche === 'vera8') {
            const basis = testheft.basis_schwierigkeit ?? null;
            const ergaenzung = testheft.ergaenzung_schwierigkeit ?? null;
            let schwierigkeit = ergaenzung ?? testheft.schwierigkeit ?? null;
            if (testheft.fach === 'ma' && basis !== null && ergaenzung !== null) {
                // In Mathematik bestimmt die Kombination aus Basis- und Ergänzungsmodul das Heft A/B/C
                const kombinationen: Record<string, number> = {'1-1': 1, '1-2': 2, '2-2': 3};
                schwierigkeit = kombinationen[`${basis}-${ergaenzung}`] ?? testheft.schwierigkeit ?? null;
            }
            if (testheft.fach === 'fr' && (schwierigkeit === 1 || schwierigkeit === 2)) {
                bezeichnung = `th-${schwierigkeit}`;
            } else if (['de', 'en', 'ma'].includes(testheft.fach) && schwierigkeit !== null && schwierigkeit >= 1 && schwierigkeit <= 3) {
                bezeichnung = `th-${['a', 'b', 'c'][schwierigkeit - 1]}`;
            }
            domaenen = (testheft.domaenen ?? []).map(domaene => domaene.replace(/^[a-z]{2}-/i, '')).sort();
        }

        const teile = [testheft.fach, bezeichnung.replace(/^testheft\b/i, 'th'), ...domaenen];
        return Utilities.normalizeString(teile.join('-')).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    }

    private getTestheftExportdaten(schulenArr: IWasserstandSchule[]): Record<string, number>[] {
        const eingabestart = this.sessionvariablen.land.ergebnisdateneingabe_start_am ?? null;
        const mitAbschluss = eingabestart !== null && this.portalzeit >= new Date(eingabestart);
        // Der Testmodus richtet sich auch beim Filterexport nach dem gesamten geladenen Schulbestand
        const nurCbt = this.sessionvariablen.projekt.weiche === 'vera5' && !this.schulen.some(schule => (schule.testheftstatistik ?? []).some(testheft => testheft.pp > 0));
        const kennzahlen: {key: keyof IWasserstandTestheftAnzahlen; label: string; abschluss?: boolean}[] = nurCbt ? [
            {key: 'testgruppen_cbt', label: 'testgruppen'},
            {key: 'total', label: 'tests_angemeldet'},
            {key: 'cbt_zusatzlieferung', label: 'tests_zusatzlieferung_cbt'},
            {key: 'abgeschlossen', label: 'tests_abgeschlossen', abschluss: true}
        ] : [
            {key: 'testgruppen_pp', label: 'testgruppen_pp'},
            {key: 'testgruppen_cbt', label: 'testgruppen_cbt'},
            {key: 'total', label: 'tests_angemeldet'},
            {key: 'pp', label: 'tests_angemeldet_pp'},
            {key: 'cbt', label: 'tests_angemeldet_cbt'},
            {key: 'cbt_zusatzlieferung', label: 'tests_zusatzlieferung_cbt'},
            {key: 'abgeschlossen', label: 'tests_abgeschlossen', abschluss: true},
            {key: 'abgeschlossen_pp', label: 'tests_abgeschlossen_pp', abschluss: true},
            {key: 'abgeschlossen_cbt', label: 'tests_abgeschlossen_cbt', abschluss: true}
        ];

        const testheftnamen = new Map<number, string>();
        for (const schule of [...this.schulen, ...schulenArr]) {
            for (const testheft of schule.testheftstatistik ?? []) {
                if (!testheftnamen.has(testheft.testheft_id)) {
                    testheftnamen.set(testheft.testheft_id, this.getTestheftExportbezeichner(testheft));
                }
            }
        }

        const summen = new Map<number, IWasserstandTestheftstatistik>();
        for (const schule of schulenArr) {
            for (const testheft of schule.testheftstatistik ?? []) {
                const summe = summen.get(testheft.testheft_id) ?? null;
                if (summe === null) {
                    summen.set(testheft.testheft_id, {...testheft});
                    continue;
                }
                for (const kennzahl of kennzahlen) {
                    summe[kennzahl.key] += testheft[kennzahl.key];
                }
            }
        }

        const testhefte = [...summen.values()].sort((a, b) => a.fach.localeCompare(b.fach) || a.zepf_testheft_id.localeCompare(b.zepf_testheft_id));
        const spalten: {testheftId: number; key: keyof IWasserstandTestheftAnzahlen; label: string}[] = [];
        for (const testheft of testhefte) {
            const kurzname = testheftnamen.get(testheft.testheft_id) ?? this.getTestheftExportbezeichner(testheft);
            // Nur gleichnamige Varianten benötigen eine zusätzliche kurze Kennung
            const mehrfach = [...testheftnamen.values()].filter(name => name === kurzname).length > 1;
            const prefix = mehrfach ? `${kurzname}-v${testheft.testheft_id}` : kurzname;
            for (const kennzahl of kennzahlen) {
                if (kennzahl.abschluss ? !mitAbschluss : testheft[kennzahl.key] === 0) continue;
                spalten.push({testheftId: testheft.testheft_id, key: kennzahl.key, label: `${prefix}_${kennzahl.label}_anz`});
            }
        }

        return schulenArr.map(schule => {
            const statistik = new Map((schule.testheftstatistik ?? []).map(testheft => [testheft.testheft_id, testheft]));
            const werte: Record<string, number> = {};
            for (const spalte of spalten) {
                werte[spalte.label] = statistik.get(spalte.testheftId)?.[spalte.key] ?? 0;
            }
            return werte;
        });
    }

    private schulenToCsv(schulenArr: IWasserstandSchule[], umfang: 'komplett' | 'gefiltert' = 'komplett'): void {
        const jahrgang = schulenArr[0]?.jahrgang ?? this.portalzeit.getFullYear();
        const filename = `${this.sessionvariablen.projekt.weiche}-${jahrgang}-druckbedarfsexport-${umfang}.csv`;
        const testheftExportdaten = this.getTestheftExportdaten(schulenArr);
        const mitAdressaenderungen = this.sessionvariablen.schule.land === 'nw' && this.sessionvariablen.projekt.weiche !== 'vera5';
        Utilities.exportJsonToCsv(schulenArr.map((s, index) => {
            return {
                'schulnummer': s.schulnummer,
                'schulform': s.schulform,
                'teilnahmestatus': (s.statustyp === 1) ? 'verpflichtend' : (s.statustyp === 2) ? 'freiwillig' : 'deaktiviert',
                ...((this.sessionvariablen.schule.land === 'nw') ? {
                    'testmodus': (s.is_cbt_schule_mathematik === 1) ? 'CBT' : (s.is_cbt_schule_mathematik === 0) ? 'PP' : 'keine Angabe',
                } : {}),

                ...(!mitAdressaenderungen ? {
                    'schulname': s.schulname,
                    'strasse_hausnummer': s.strasse_nr,
                    'plz': s.plz,
                    'ort': s.ort,
                    'email': s.email,
                    'telefon': s.telefon,
                } : {}),
                ...(mitAdressaenderungen ? {
                    'schulname_urspruenglich': s.schulname,
                    'strasse_hausnummer_urspruenglich': s.strasse_nr,
                    'plz_urspruenglich': s.plz,
                    'ort_urspruenglich': s.ort,
                    'email_urspruenglich': s.email,
                    'telefon_urspruenglich': s.telefon,

                    'schulname_aktualisiert': s.schulname_angepasst ?? s.schulname,
                    'strasse_hausnummer_aktualisiert': s.strasse_nr_angepasst ?? s.strasse_nr,
                    'plz_aktualisiert': s.plz_angepasst ?? s.plz,
                    'ort_aktualisiert': s.ort_angepasst ?? s.ort,
                    'email_aktualisiert': s.email_angepasst ?? s.email,
                    'telefon_aktualisiert': s.telefon_angepasst ?? s.telefon,

                    'kontaktdaten_geaendert': (
                        (s.schulname_angepasst ?? s.schulname) !== s.schulname ||
                        (s.strasse_nr_angepasst ?? s.strasse_nr) !== s.strasse_nr ||
                        (s.plz_angepasst ?? s.plz) !== s.plz ||
                        (s.ort_angepasst ?? s.ort) !== s.ort ||
                        (s.email_angepasst ?? s.email) !== s.email ||
                        (s.telefon_angepasst ?? s.telefon) !== s.telefon
                    ) ? 'x' : 'o'
                } : {}),

                'schueler_anz': s.n_schueler_insgesamt,
                'stammklassen_anz': s.n_stammklassen_insgesamt,
                ...((this.sessionvariablen.schule.land !== 'ni') ? {
                    'kurse_anz': s.n_kurse_insgesamt,
                } : {}),
                ...((this.sessionvariablen.schule.land === 'ni') ? {
                    'lerngruppen_anz': s.n_lerngruppen_insgesamt,
                } : {}),
                ...((this.sessionvariablen.schule.land === 'nw' && this.sessionvariablen.projekt.weiche === 'vera8') ? {
                    'lerngruppen_fr_9_anz': s.n_lerngruppen_insgesamt,
                } : {}),

                'schule_vollstaendig': (s.n_schueler_insgesamt > 0 && s.anzahlWarnungen === 0) ? 'x' : 'o',
                'schule_unbearbeitet': (s.n_schueler_insgesamt === 0) ? 'x' : 'o',

                ...testheftExportdaten[index],

                'hat_warnungen': (s.anzahlWarnungen > 0) ? 'x' : 'o',
                'warnungen_anz': s.anzahlWarnungen ?? 0,
                'schueler_ohne_pflichttest_anz': s.n_schueler_ohne_verpflichtende_testhefte ?? 0,

                'warnung_geschlecht_fehlt': (s.is_ok_schueler_ohne_geschlecht_warnung === 0) ? 'x' : 'o',
                'warnung_stammklasse_fehlt': (s.is_ok_schueler_ohne_stammklasse_warnung === 0) ? 'x' : 'o',
                'warnung_stammklasse_leer': (s.is_ok_stammklassen_ohne_schueler_warnung === 0) ? 'x' : 'o',
                'warnung_kurs_lerngruppe_leer': (s.is_ok_kurse_ohne_schueler_warnung === 0) ? 'x' : 'o',

                'warnung_testanmeldung_ma_fehlt': (s.is_ok_schueler_ohne_mathematik_testheft_warnung === 0) ? 'x' : 'o',
                'warnung_testanmeldung_de_fehlt': (s.is_ok_schueler_ohne_deutsch_testheft_warnung === 0) ? 'x' : 'o',
                'warnung_testanmeldung_en_fehlt': (s.is_ok_schueler_ohne_englisch_testheft_warnung === 0) ? 'x' : 'o',
                'warnung_testanmeldung_fr_fehlt': (s.is_ok_schueler_ohne_franzoesisch_testheft_warnung === 0) ? 'x' : 'o',

                'warnung_ergebnisse_ma_unvollstaendig': (s.is_ok_unvollstaendige_tests_mathematik_warnung === 0) ? 'x' : 'o',
                'warnung_ergebnisse_de_unvollstaendig': (s.is_ok_unvollstaendige_tests_deutsch_warnung === 0) ? 'x' : 'o',
                'warnung_ergebnisse_en_unvollstaendig': (s.is_ok_unvollstaendige_tests_englisch_warnung === 0) ? 'x' : 'o',
                'warnung_ergebnisse_fr_unvollstaendig': (s.is_ok_unvollstaendige_tests_franzoesisch_warnung === 0) ? 'x' : 'o',

                'schueler_geaendert_am': s.letzte_aenderung_schueler ?? '-',
                'ergebnisse_geaendert_am': s.letzte_aenderung_ergebnisse ?? '-',
            }
        }), filename, '\t');
    }
}
